import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { useGetMe } from "@/api";
import { useProfile } from "@/hooks/useProfile";
import {
  profileAccountSchema,
  profilePasswordSchema,
  type ProfileAccountFormValues,
  type ProfilePasswordFormValues,
} from "@/lib/validation/profile.schemas";

export function useProfilePage() {
  const { profile, isLoading, isUpdating, isChangingPassword, updateUser, changePassword } =
    useProfile();
  const { data: meData } = useGetMe();
  const stores = meData?.status === 200 ? meData.data.data.stores : [];

  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const accountForm = useForm<ProfileAccountFormValues>({
    resolver: zodResolver(profileAccountSchema),
    defaultValues: { full_name: "", username: "", phone: "" },
  });

  useEffect(() => {
    if (profile) {
      accountForm.reset({
        full_name: profile.full_name ?? "",
        username: profile.username ?? "",
        phone: profile.phone ?? "",
      });
    }
  }, [profile, accountForm]);

  const passwordForm = useForm<ProfilePasswordFormValues>({
    resolver: zodResolver(profilePasswordSchema),
    defaultValues: { current_password: "", new_password: "", confirm_password: "" },
  });

  const onAccountSubmit = accountForm.handleSubmit((values) => {
    if (!profile) return;
    updateUser({ id: String(profile.id), data: values });
  });

  const onPasswordSubmit = passwordForm.handleSubmit((values) => {
    changePassword(
      { data: { current_password: values.current_password, new_password: values.new_password } },
      {
        onSuccess: () => {
          setPasswordSuccess(true);
          passwordForm.reset();
        },
      },
    );
  });

  return {
    isLoading,
    isUpdating,
    isChangingPassword,
    passwordSuccess,
    stores,
    accountForm,
    passwordForm,
    onAccountSubmit,
    onPasswordSubmit,
  };
}
